import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import SEOMetadata from '@/components/SEOMetadata';
import JsonLd from '@/components/JsonLd';
import Loader from '@/Loader';
import { breakpoints } from '@/layouts/breakpoints';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';
import { ORIGIN } from '@/lib/schema';
import { tickerIndex } from '@/generated/tickerIndex';

const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) return `rgba(77, 66, 216, ${alpha})`;
  return `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, ${alpha})`;
}

interface TickerEntry {
  ticker: string;
  name: string;
  sector: string;
  exchange: string;
  logoUrl: string;
  accent: string;
}

interface LetterGroup {
  letter: string;
  entries: TickerEntry[];
}

const letterGroups: LetterGroup[] = ALPHABET.map((letter) => ({
  letter,
  entries: tickerIndex
    .filter((t) => t.ticker.charAt(0).toUpperCase() === letter)
    .sort((a, b) => a.ticker.localeCompare(b.ticker)),
})).filter((g) => g.entries.length > 0);

const itemListSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'AxionQuant Stock Reports',
  numberOfItems: tickerIndex.length,
  itemListElement: tickerIndex.map((t, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    url: `${ORIGIN}/stock/${t.ticker}`,
    name: `${t.name} (${t.ticker})`,
  })),
};

const TickerLogo: React.FC<{ src: string; ticker: string; accent: string }> = ({ src, ticker, accent }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return <LogoTile style={{ background: hexToRgba(accent, 0.12), color: accent }}>{ticker.charAt(0)}</LogoTile>;
  }
  return (
    <LogoTile style={{ background: hexToRgba(accent, 0.08) }}>
      <img src={src} alt={`${ticker} logo`} loading="lazy" width={28} height={28} onError={() => setFailed(true)} />
    </LogoTile>
  );
};

const StockCard: React.FC<{ entry: TickerEntry }> = ({ entry }) => (
  <CardLink to={`/stock/${entry.ticker}`} aria-label={`${entry.name} (${entry.ticker}) stock report`}>
    <TickerLogo src={entry.logoUrl} ticker={entry.ticker} accent={entry.accent} />
    <CardBody>
      <CardTicker>
        {entry.ticker}
        <CardExchange>:{entry.exchange}</CardExchange>
      </CardTicker>
      <CardSector>{entry.sector || entry.name}</CardSector>
    </CardBody>
  </CardLink>
);

function StocksHubPage() {
  const [activeLetter, setActiveLetter] = useState(letterGroups[0]?.letter ?? 'A');
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  useIdlePrefetch([loadGetStarted, loadFooter]);

  const grouped = useMemo(() => letterGroups, []);

  useEffect(() => {
    const onScroll = () => {
      let current = grouped[0]?.letter ?? 'A';
      for (const group of grouped) {
        const el = sectionRefs.current[group.letter];
        if (el && el.getBoundingClientRect().top <= 220) {
          current = group.letter;
        }
      }
      setActiveLetter(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [grouped]);

  const jumpTo = (letter: string) => {
    const el = sectionRefs.current[letter];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveLetter(letter);
    }
  };

  return (
    <>
      <SEOMetadata
        title="Stock Research & Reports"
        description={`In-depth research on ${tickerIndex.length} popular US-listed stocks: fundamentals, alternative data, valuation and market data. Thousands more via the AxionQuant API.`}
        canonicalUrl={`${ORIGIN}/stocks`}
        ogType="website"
      />
      <JsonLd data={itemListSchema} />

      <Wrapper>
        <Container>
          <Eyebrow>Stock Reports</Eyebrow>
          <Title className="serif">Stock Research & Reports</Title>
          <Subtitle>
            In-depth analysis on widely followed US-listed companies. Fundamentals,
            alternative data and developer-ready market data. Thousands more available through the AxionQuant API.
          </Subtitle>
          <TitleRule />

          <Layout>
            <ListColumn>
              {grouped.map(({ letter, entries }) => (
                <LetterSection
                  key={letter}
                  id={`stocks-letter-${letter}`}
                  ref={(el: HTMLElement | null) => {
                    sectionRefs.current[letter] = el;
                  }}
                >
                  <LetterHeading className="serif">
                    {letter}
                    <LetterCount>{entries.length}</LetterCount>
                  </LetterHeading>
                  <CardsGrid>
                    {entries.map((entry) => (
                      <StockCard key={entry.ticker} entry={entry} />
                    ))}
                  </CardsGrid>
                </LetterSection>
              ))}
            </ListColumn>

            <RailColumn>
              <Rail role="navigation" aria-label="Jump to letter">
                {ALPHABET.map((letter) => {
                  const available = grouped.some((g) => g.letter === letter);
                  return (
                    <RailButton
                      key={letter}
                      type="button"
                      disabled={!available}
                      $active={activeLetter === letter}
                      onClick={() => jumpTo(letter)}
                      aria-label={available ? `Jump to stocks starting with ${letter}` : undefined}
                      tabIndex={available ? 0 : -1}
                    >
                      {letter}
                    </RailButton>
                  );
                })}
              </Rail>
            </RailColumn>
          </Layout>
        </Container>

        <Suspense fallback={<Loader />}>
          <GetStarted />
        </Suspense>
      </Wrapper>
      <Suspense fallback={<Loader />}>
        <Footer />
      </Suspense>
    </>
  );
};

export default StocksHubPage;

const Wrapper = styled.div`
  min-height: 100vh;
  background: #fff;
`;

const Container = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  padding: 160px 24px 96px;

  @media (max-width: ${breakpoints.medium}) {
    padding: 80px 16px 64px;
  }
`;

const Eyebrow = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 12px;
`;

const Title = styled.h1`
  font-size: 38px !important;
  color: #111;
  margin: 0 0 12px;
  letter-spacing: -0.02em;

  @media (max-width: ${breakpoints.medium}) {
    font-size: 26px !important;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: rgba(0, 0, 0, 0.55);
  margin: 0 0 24px;
  max-width: 560px;
  line-height: 1.6;
`;

const TitleRule = styled.div`
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  margin: 0 0 40px;
`;

const Layout = styled.div`
  display: flex;
  gap: 28px;
  align-items: flex-start;

  @media (max-width: ${breakpoints.medium}) {
    gap: 12px;
  }
`;

const ListColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

const LetterSection = styled.section`
  scroll-margin-top: 170px;

  & + & {
    margin-top: 44px;
  }
`;

const LetterHeading = styled.h2`
  font-size: 20px;
  color: #111;
  margin: 0 0 14px;
  letter-spacing: -0.01em;
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);
`;

const LetterCount = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.4);
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 10px;

  @media (max-width: ${breakpoints.small}) {
    grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  }
`;

const CardLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid rgba(0, 0, 0, 0.09);
  border-radius: 10px;
  background: #fff;
  text-decoration: none;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.03);
    border-color: rgba(0, 0, 0, 0.22);
  }

  &:focus-visible {
    outline: 2px solid #111;
    outline-offset: 2px;
  }
`;

const LogoTile = styled.div`
  flex: none;
  width: 44px;
  height: 44px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  font-weight: 700;
  overflow: hidden;

  img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
`;

const CardBody = styled.div`
  min-width: 0;
`;

const CardTicker = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111;
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardExchange = styled.span`
  color: rgba(0, 0, 0, 0.45);
  font-weight: 500;
  font-size: 12px;
`;

const CardSector = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RailColumn = styled.div`
  position: sticky;
  top: 190px;
  flex: none;

  @media (max-width: ${breakpoints.medium}) {
    display: none;
  }
`;

const Rail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RailButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 22px;
  border: none;
  padding: 0;
  background: transparent;
  font-size: 11px;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.45);
  transition: color 0.2s ease;

  &:hover:not(:disabled) {
    color: #111;
  }

  &:disabled {
    color: rgba(0, 0, 0, 0.18);
    cursor: default;
  }

  ${(p) =>
    p.$active &&
    `
      font-weight: 700;
      color: #111;
    `}
`;
